import * as R from 'ramda'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { usePanelHistoryTop } from 'weblite-router'
import { Message, MessageActionTypes } from '@weblite/ts-action/message'
import { PendingUpload } from '$logic/pending-uploads/types'
import { PendingMessage } from '$logic/pending-messages/types'
import {
  getOpenedRoomId,
  openedRoomPermissions,
} from '$logic/opened-room/opened-room.service'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { RootState } from '$setup/redux'
import { useOpenedRoomPendingUploads } from '$logic/pending-uploads/react-hook'
import { useOpenedRoomPendingMessages } from '$logic/pending-messages/react-hook'
import { useRoom } from '$logic/room/react-hook'
import { useMessageAction } from '$logic/message-action/react-hook'
import { useRoomLastMessage } from '$logic/room-last-message/react-hook'
import { OpenedRoomMessageToScroll } from '$logic/opened-room/opened-room.storage'
import { decodePendingId } from '$logic/message'
import { useSubscriptionSyncPermissions } from '$logic/subscription/react-hook'

export function useOpenedRoomId(): string {
  return usePanelHistoryTop('room')?.state?.roomId || ''
}

export function useOpenedRoom() {
  const roomId = useOpenedRoomId()
  const room = useRoom(roomId)
  return { openedRoom: room, loading: !room }
}

export function useOpenedRoomPermissions() {
  const roomId = useOpenedRoomId()
  const permissions = useSubscriptionSyncPermissions(roomId)
  const { value: openedRoomPermissionsValue, loading } = useAsyncFunction(
    openedRoomPermissions,
    [roomId, permissions],
  )

  return {
    openedRoomPermissions: openedRoomPermissionsValue,
    loading,
    permissions,
  }
}

export function useOpenedRoomUniquePendingAndUploadMessages(
  lastMessage: Message | undefined,
) {
  const lastMessageId = lastMessage ? decodePendingId(lastMessage) : null
  const pendingUploads = useOpenedRoomPendingUploads()
  const pendingMessages = useOpenedRoomPendingMessages()
  const pendingMessagesIds = useMemo(
    () => pendingMessages.map(({ id }) => id),
    [pendingMessages],
  )

  return useMemo(
    () => [
      ...pendingUploads.filter(
        ({ id }) => !pendingMessagesIds.includes(id) && !(id === lastMessageId),
      ),
      ...pendingMessages.filter(({ id }) => id !== lastMessageId),
    ],
    [pendingMessagesIds, lastMessageId, pendingMessages, pendingUploads],
  )
}
/**
 * TODO: Sending message cause 5 times re-rendering. (3 for text message)
 *  1. Save pending upload
 *  2. Remove pending upload
 *  3. Save pending message
 *  4. Remove pending message
 *  5. Save message
 */

export function useOpenedRoomSingleMessageByIid(
  roomId: string,
  iid: number,
): Message | null {
  return useSelector(openedRoomMessageByIidView(roomId, iid))
}

const openedRoomMessageByIidView = R.memoizeWith(
  (roomId, iid) => `${roomId}-${iid}`,
  (roomId, iid) => (state: RootState) =>
    getOpenedRoomId() === roomId
      ? (state.main.openedRoomMessages as Message[]).find(
          message => message.roomId === roomId && message.iid === iid,
        )
      : null,
)

export function useOpenedRoomMessages(): (
  | Message
  | PendingMessage
  | PendingUpload
)[] {
  const { openedRoom: room } = useOpenedRoom()
  const messages: Message[] = useSelector(
    (state: RootState) => state.main.openedRoomMessages,
  )

  const pendingUploadsAndPendingMessages =
    useOpenedRoomUniquePendingAndUploadMessages(R.last(messages || []))

  const lastMessage = useRoomLastMessage(room?.id)

  return useMemo(
    () =>
      // NOTE: If last message of room is in list, insert pending
      R.findLast(R.propEq('iid', lastMessage?.iid), messages)
        ? [
            ...messages,
            ...R.sortBy(R.prop('pendingDate'), [
              ...pendingUploadsAndPendingMessages,
            ]),
          ]
        : messages,
    [pendingUploadsAndPendingMessages, messages, lastMessage?.iid],
  )
}

export function useOpenedRoomLoading() {
  return useSelector((state: RootState) => state.main.openedRoom.loading)
}

export function useOpenedRoomFirstUnseenMessageIid() {
  return useSelector(
    (state: RootState) => state.main.openedRoom.firstUnseenMessageIid,
  )
}

export function useOpenedRoomMessageToScroll() {
  return useSelector(
    (state: RootState) => state.main.openedRoom.messageToScroll,
  ) as OpenedRoomMessageToScroll
}

export function useOpenedRoomMessageAction(): {
  userId: string
  action: MessageActionTypes
}[] {
  const roomId = useOpenedRoomId()
  return useMessageAction(roomId)
}
